import type { ExpressionSpecification } from 'maplibre-gl'
import type { AreaFeature } from './operatingAreas'

/**
 * Build a MapLibre `within` filter expression that keeps only features whose
 * geometry lies inside the given area: the union of the shown geonets and the
 * operating area, i.e. the part of the map the operating-area shade leaves clear.
 *
 * Why: lanes stream from the PMTiles `segments` road graph, and at far (low)
 * zoom the archive packs the whole metro into a single, coarsely-quantized tile
 * — so the lanes render as a torn mass. MapLibre can't overzoom downward to
 * recover detail, so instead we confine lanes to the (small) unshaded area. This
 * is also the product behaviour we want: lanes never show in the shaded part of
 * the map and appear/disappear as geonet zones are toggled.
 *
 * Pass the areas unioned (`mergeAreaFeatures`), not collected into a
 * MultiPolygon: `within` only keeps a line that lies inside one single polygon,
 * so a lane crossing from the operating area into an overlapping geonet would
 * otherwise be dropped. Returns null without an area — lanes stay unconfined.
 */
export const buildLaneClipExpression = (
  area: AreaFeature | null,
): ExpressionSpecification | null => {
  if (!area) return null
  // `within` accepts Polygon/MultiPolygon/Feature/FeatureCollection; the geometry
  // object isn't part of the expression tuple type, hence the cast.
  return ['within', area.geometry] as unknown as ExpressionSpecification
}
